import type { TeaserListItem, Asset, AuthorData } from '~/@types/cms';

// https://pinia.vuejs.org/core-concepts/#setup-stores
export const useMicroDataStore = defineStore('woom-store-micro-data', () => {
    const appStore = useAppStore();
    const config = useRuntimeConfig();
    const route = useRoute();
    const router = useRouter();
    const { isUS, country } = useLocales();

    const { socialLinks } = storeToRefs(appStore);

    const baseUrl = config.public.BASE_URL;

    const phoneByCountry: Record<string | 'fallback', string> = {
        at: '+43800404332',
        de: '+498004043000',
        ch: '+41800550349',
        us: '+1855-966-6872',
        fallback: '+4313585803',
    };
    const sloganEN = 'woom kids’ bikes – your child will love them.';
    const sloganDE = 'woom Kinderfahrrad – dein Kind wird es lieben!';
    const sloganByCountry: Record<string | 'fallback', string> = {
        de: sloganDE,
        at: sloganDE,
        ch: sloganDE,
        nl: 'woom kinderfietsen – dit vinden kinderen leuk!',
        fr: 'Vélos pour enfants woom – votre enfant va les adorer !',
        sv: 'woom barncyklar – ditt barn kommer att älska dem!',
        dk: 'woom børnecykler – dit barn vil elske dem!',
        fallback: sloganEN,
    };

    const award: string[] = [
        '2024 Good Housekeeping Parenting Award',
        'Bicycle Brand Contest 2017',
        'German Design Award 2018',
        "A' Design Award 2018",
        'Reddot design award 2017',
        'iF Design Award 2020',
        'Design & Innovation Award 2020',
        'Bicycling USA Bike Award 2020',
        'Bike Award 2019 Polen',
        'German Design 2021 Award Special Mention',
        'German Design Award 2021',
        'Focus E-Bike 2020 Design & Innovation Award',
        'Design & Innovation Award 2022',
        'Taipei Bicycle Award 2022',
        'Red Dot Award 2022',
        'Safety Bike Award 2019 Poland',
        'Good Design Award 2018',
        'Design & Innovation Award 2018',
        'iF Design Award 2018',
        'Eurobike Award 2018',
    ];

    const getAuthorfromAuthorData = (data?: AuthorData) => {
        if (!data) return undefined;
        return {
            '@type': 'Person',
            name: data.name,
            image: data.image,
        } as const;
    };
    const getImageFromAsset = (asset: Asset) => {
        const url = resolveAssetSrc(asset, '16to9');
        if (!asset || !url) return;
        if (typeof asset !== 'string' && isAssetKontentAi(asset)) {
            return {
                '@type': 'ImageObject',
                '@id': asset.data.src,
                url,
                width: asset.data.width,
                height: asset.data.height,
            } as const;
        }

        return {
            '@type': 'ImageObject',
            '@id': url,
            url,
        } as const;
    };

    const regionalValue = <USType, EUType>(usValue: USType, euValue: EUType) => (isUS.value ? usValue : euValue);

    router.beforeEach(() => {
        contentType.value = undefined;
        headerData.value = undefined;
        blogPostsData.value = [];
    });

    /**
     * Old model: https://gitlab.woombikes.com/website/woom-lib-model/-/blob/master/src/model/Taxonomies/ContentType.ts
     *
     * New model: https://gitlab.woombikes.com/website/woom-webhooks-kontentai/-/blob/main/src/woom-lib-model/src/model/Taxonomies/ContentType.ts
     */
    type ContentTypes = 'blog_listing' | 'blog_post' | 'press_listing' | 'press_post';
    const contentType = ref<ContentTypes>();
    const dateModified = ref<string>();
    const setBaseData = ({ type, dateModified: _dateModified }: { type?: string; dateModified: string }) => {
        contentType.value = type as ContentTypes | undefined;
        dateModified.value = _dateModified;
    };

    type Header = {
        name: string;
        description?: string;
        datePublished?: string;
        author?: AuthorData;
        asset?: Asset;
    };
    const headerData = ref<Header>();
    const setHeader = (data: Header) => {
        if (headerData.value) return;
        headerData.value = data;
    };

    const blogPostsData = ref<TeaserListItem[]>([]);
    const addBlogPostsData = (data: TeaserListItem[]) => {
        blogPostsData.value = blogPostsData.value.concat(data);
    };

    useJsonld(() => {
        if (contentType.value !== 'blog_listing' || !headerData.value || !blogPostsData.value.length) return null;
        return {
            '@context': 'https://schema.org',
            '@type': 'Blog',
            name: headerData.value.name,
            description: headerData.value.description,
            datePublished: headerData.value.datePublished,
            blogPost: blogPostsData.value.map((item) => {
                return {
                    '@type': 'BlogPosting',
                    headline: item.title,
                    description: item.subtitle,
                    datePublished: item.date || undefined,
                    author: getAuthorfromAuthorData(item.author),
                    image: getImageFromAsset(item.hero),
                    url: `${baseUrl}${item.url}`,
                };
            }),
        };
    });

    useJsonld(() => {
        if (!contentType.value || !['blog_post', 'press_post'].includes(contentType.value) || !headerData.value) return null;
        const type = contentType.value === 'blog_post' ? 'BlogPosting' : 'NewsArticle';
        const url = `${baseUrl}${route.fullPath}`;
        return {
            '@context': 'https://schema.org',
            '@type': type,
            '@id': url,
            mainEntityOfPage: url,
            headline: headerData.value.name,
            description: headerData.value.description,
            datePublished: headerData.value.datePublished,
            author: getAuthorfromAuthorData(headerData.value.author),
            image: getImageFromAsset(headerData.value.asset),
            url,
        };
    });

    useJsonld(() => {
        if (contentType.value !== 'press_listing' || !headerData.value || !blogPostsData.value.length) return null;
        const url = `${baseUrl}${route.fullPath}`;
        return {
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: headerData?.value.name,
            description: headerData?.value?.description,
            url: url,
            hasPart: blogPostsData.value.map((item) => {
                return {
                    '@type': 'NewsArticle',
                    headline: item.title,
                    description: item.subtitle,
                    datePublished: item.date || undefined,
                    url: `${baseUrl}${item.url}`,
                    author: getAuthorfromAuthorData(item.author),
                    image: getImageFromAsset(item.hero),
                    isPartOf: {
                        '@type': 'CollectionPage',
                        url: url,
                    },
                };
            }),
        };
    });

    const socialLinksOnly = computed(() => socialLinks.value.map((link) => link.url));
    useJsonld(() => ({
        '@context': 'https://schema.org',
        '@type': 'Organization',
        url: baseUrl,
        sameAs: socialLinksOnly.value,
        logo: `${baseUrl}/woom-logo.svg`,
        name: 'woom',
        legalName: 'woom GmbH',
        // TODO: add with https://onewoom.atlassian.net/browse/ECOMM-2504
        // description: 'Text from CMS',
        email: regionalValue('usa@woom.com', 'woom@woom.com'),
        founder: [
            {
                '@type': 'Person',
                name: 'Marcus Ihlenfeld',
            },
            {
                '@type': 'Person',
                name: 'Christian Bezdeka',
            },
        ],
        foundingDate: '2013',
        foundingLocation: {
            '@type': 'Place',
            name: 'Vienna, Austria',
        },
        slogan: sloganByCountry[country.value] || sloganByCountry['fallback'],
        award,
        telephone: phoneByCountry[country.value] || phoneByCountry['fallback'],
        address: {
            '@type': 'PostalAddress',
            streetAddress: regionalValue('8301 Springdale Rd, Ste. 800', 'Muthgasse 109 A'),
            addressLocality: regionalValue('Austin', 'Vienna'),
            addressRegion: regionalValue('TX', undefined),
            postalCode: regionalValue('78724', '1100'),
            addressCountry: regionalValue('US', 'AT'),
            areaServed: regionalValue(['US', 'CA'], undefined),
        },
        contactPoint: [
            {
                '@type': 'ContactPoint',
                contactType: 'Customer Service',
                email: regionalValue('usa@woombikes.com', 'woom@woom.com'),
                telephone: phoneByCountry[country.value] || phoneByCountry['fallback'],
                availableLanguage: regionalValue('English', ['Danish', 'German', 'French', 'Swedish', 'Dutch']),
            },
            {
                '@type': 'ContactPoint',
                contactType: 'Media Inquiries',
                email: regionalValue('usa@woombikes.com', 'woom@woom.com'),
                telephone: phoneByCountry[country.value] || phoneByCountry['fallback'],
                availableLanguage: regionalValue('English', ['Danish', 'German', 'French', 'Swedish', 'Dutch']),
            },
        ],
        vatID: regionalValue(undefined, 'ATU67805716'),
    }));

    return { setBaseData, setHeader, addBlogPostsData };
});

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useMicroDataStore, import.meta.hot));
}
